import React, { useState, useEffect, useRef } from 'react';
import { sendMessage, getIstoricChat } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  red_flag?: boolean;
}

interface Conversatie {
  conversatie_id: string;
  titlu: string;
  data: string;
}

const renderMarkdown = (text: string) => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
};

const Chat: React.FC = () => {
  const { isAnonim } = useAuthStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversatieId, setConversatieId] = useState<string | undefined>();
  const [istoric, setIstoricList] = useState<Conversatie[]>([]);
  const [istoricDeschis, setIstoricDeschis] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [redFlagActiv, setRedFlagActiv] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadIstoric();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIstoricDeschis(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  const loadIstoric = async () => {
    try {
      const res = await getIstoricChat();
      setIstoricList(res.data);
    } catch {}
  };

  const chatNou = () => {
    setMessages([]);
    setConversatieId(undefined);
    setRedFlagActiv(false);
    if (isMobile) setIstoricDeschis(false);
  };

  const incarcaConversatie = async (conv: Conversatie) => {
    try {
      const { incarcaConversatieById } = await import('../services/api');
      const res = await incarcaConversatieById(conv.conversatie_id);
      setMessages(res.data);
      setConversatieId(conv.conversatie_id);
      setRedFlagActiv(false);
      if (isMobile) setIstoricDeschis(false);
    } catch {
      toast.error('Eroare la încărcarea conversației!');
    }
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const mesaj = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setMessages(prev => [...prev, { role: 'user', content: mesaj }]);
    setLoading(true);
    try {
      const res = await sendMessage(mesaj, conversatieId);
      const { reply, conversatie_id, red_flag } = res.data;
      setConversatieId(conversatie_id);
      setMessages(prev => [...prev, { role: 'assistant', content: reply, red_flag: !!red_flag }]);
      if (red_flag) setRedFlagActiv(true);
      loadIstoric();
    } catch {
      toast.error('Eroare la trimitere!');
    } finally {
      setLoading(false);
    }
  };

  const IstoricContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '1rem', borderBottom: '1px solid #1e1e3a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ color: '#8080a0', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
          Conversații
        </p>
        {isMobile && (
          <button onClick={() => setIstoricDeschis(false)} style={{
            background: 'transparent', border: 'none', color: '#a0a0c0',
            fontSize: '1.2rem', cursor: 'pointer', padding: '0.25rem'
          }}>✕</button>
        )}
      </div>
      <div style={{ padding: '0.75rem' }}>
        <button onClick={chatNou} style={{
          width: '100%', padding: '0.5rem', borderRadius: '8px',
          border: 'none', background: '#4a4ad8', color: '#fff',
          fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem'
        }}>
          + Chat nou
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 0.5rem 0.5rem' }}>
        {istoric.length === 0 && (
          <p style={{ color: '#4a4a6a', fontSize: '0.8rem', textAlign: 'center', marginTop: '1rem' }}>
            Nicio conversație încă
          </p>
        )}
        {istoric.map(c => (
          <button key={c.conversatie_id} onClick={() => incarcaConversatie(c)} style={{
            width: '100%', background: 'transparent', color: '#a0a0c0',
            border: 'none', borderRadius: '8px', padding: '0.5rem 0.75rem',
            cursor: 'pointer', textAlign: 'left', fontSize: '0.8rem',
            marginBottom: '2px', transition: 'background 0.15s'
          }}>
            <span style={{ color: '#4a4a6a', fontSize: '0.72rem', display: 'block' }}>{c.data}</span>
            {c.titlu}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', position: 'relative' }}>

      {/* MOBILE — overlay background cand istoric e deschis */}
      {isMobile && istoricDeschis && (
        <div
          onClick={() => setIstoricDeschis(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', zIndex: 40
          }}
        />
      )}

      {/* DESKTOP — sidebar istoric normal */}
      {!isMobile && (
        <div style={{
          width: istoricDeschis ? '260px' : '0px',
          minWidth: istoricDeschis ? '260px' : '0px',
          background: '#0d0d1f',
          borderRight: istoricDeschis ? '1px solid #1e1e3a' : 'none',
          overflow: 'hidden',
          transition: 'all 0.25s ease',
          position: 'relative'
        }}>
          {istoricDeschis && <IstoricContent />}
          <button onClick={() => setIstoricDeschis(!istoricDeschis)} style={{
            position: 'absolute', right: '0px', top: '24px',
            width: '24px', height: '24px', borderRadius: '50%',
            background: '#1e1e3a', border: '1px solid #2a2a4a',
            color: '#a0a0c0', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.7rem', zIndex: 10, padding: 0
          }}>
            {istoricDeschis ? '◀' : '▶'}
          </button>
        </div>
      )}

      {/* MOBILE — sidebar istoric ca drawer din stanga */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: '280px',
          background: '#0d0d1f',
          borderRight: '1px solid #1e1e3a',
          zIndex: 50,
          transform: istoricDeschis ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease'
        }}>
          <IstoricContent />
        </div>
      )}

      {/* Chat area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#12121f' }}>

        {/* Header */}
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #1e1e3a',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          background: '#0a0a15'
        }}>
          <h2 style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>🩺 Flexara</h2>
          {isMobile && (
            <button onClick={() => setIstoricDeschis(true)} style={{
              background: '#1a1a30', border: '1px solid #2a2a4a',
              color: '#a0a0c0', borderRadius: '8px',
              padding: '0.4rem 0.75rem', cursor: 'pointer',
              fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem'
            }}>
              Conversații
            </button>
          )}
        </div>

        {/* BANNER GDPR — vizibil doar pentru utilizatori neautentificați */}
        {isAnonim && (
          <div style={{
            background: '#1a1a30',
            borderLeft: '3px solid #F39C12',
            borderBottom: '1px solid #1e1e3a',
            padding: '0.6rem 1rem',
          }}>
            <p style={{ color: '#a0a0c0', fontSize: '0.78rem', margin: 0, lineHeight: 1.5 }}>
              Mesajele tale sunt procesate de Anthropic (SUA) pentru generarea răspunsurilor AI.
              Nu introduci date personale sensibile în chat.
              Prin utilizarea chat-ului, ești de acord cu{' '}
              <a href="/confidentialitate" style={{ color: '#F39C12', textDecoration: 'underline' }}>
                Politica de Confidențialitate
              </a>{' '}Flexara.
            </p>
          </div>
        )}

        {/* DISCLAIMER PERSISTENT — vizibil mereu, mobile si desktop */}
        <div style={{
          background: '#0f0f20',
          borderBottom: '1px solid #1e1e3a',
          padding: '0.5rem 1rem',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#7070a0',
            fontSize: '0.78rem',
            margin: 0,
            lineHeight: 1.4
          }}>
            Flexara oferă informații educaționale, nu consultație medicală. Consultați un medic pentru orice decizie medicală.
          </p>
        </div>

        {/* BANNER RED FLAG — apare deasupra mesajelor cand e urgenta */}
        {redFlagActiv && (
          <div style={{
            background: '#3a0a0a',
            borderBottom: '2px solid #cc2222',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>!</span>
            <p style={{
              color: '#ff6060',
              fontSize: '0.875rem',
              margin: 0,
              lineHeight: 1.4,
              fontWeight: 600
            }}>
              Asistentul a identificat simptome care necesită atenție medicală urgentă.
              Dacă ai durere severă, pierdere de control vezică/intestin sau amorțeală,{' '}
              <strong>sună imediat la 112</strong>.
            </p>
          </div>
        )}

        {/* Mesaje */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '1rem 0.75rem' : '1.5rem 1rem' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#4a4a6a', marginTop: '3rem' }}>
                <p style={{ color: '#8080a0' }}>Bună! Sunt Flexara, asistentul tău pentru sănătatea coloanei.</p>
                <p style={{ color: '#6060a0', fontSize: '0.9rem' }}>Descrie-mi problema ta și te ajut!</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i}>
                {/* Banner red flag per mesaj — highlight subtil pe mesajul specific */}
                {msg.red_flag && msg.role === 'assistant' && (
                  <div style={{
                    background: '#2a0808',
                    border: '1px solid #882222',
                    borderRadius: '8px 8px 0 0',
                    padding: '0.4rem 0.75rem',
                    marginBottom: '-4px',
                    maxWidth: isMobile ? '85%' : '75%'
                  }}>
                    <p style={{ color: '#ff8080', fontSize: '0.75rem', margin: 0, fontWeight: 600 }}>
                      Atenție: acest răspuns conține recomandări de urgență medicală
                    </p>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  {msg.role === 'assistant' && (
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: msg.red_flag ? '#3a1010' : '#1a1a38',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '1rem', flexShrink: 0, marginTop: '4px'
                    }}>🩺</div>
                  )}
                  <div style={{
                    maxWidth: isMobile ? '85%' : (msg.role === 'user' ? '60%' : '75%'),
                    background: msg.role === 'user'
                      ? '#4a4ad8'
                      : (msg.red_flag ? '#1f0a0a' : '#1a1a2e'),
                    border: msg.red_flag && msg.role === 'assistant' ? '1px solid #882222' : 'none',
                    color: '#e0e0f0',
                    padding: '0.75rem 1rem',
                    lineHeight: 1.7,
                    borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                  />
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: '#1a1a38', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '1rem', flexShrink: 0
                }}>🩺</div>
                <div style={{ background: '#1a1a2e', padding: '0.75rem 1rem', borderRadius: '18px 18px 18px 4px', color: '#6060a0' }}>
                  . . .
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* Input */}
        <div style={{ padding: isMobile ? '0.75rem' : '1rem', borderTop: '1px solid #1e1e3a', background: '#0a0a15' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Descrie durerea sau problema ta..."
              rows={1}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                borderRadius: '12px',
                border: '1.5px solid #2a2a4a',
                background: '#1a1a30',
                color: '#e0e0f0',
                fontSize: '1rem',
                resize: 'none',
                overflow: 'hidden',
                lineHeight: '1.5',
                fontFamily: 'DM Sans, sans-serif',
                minHeight: '48px',
                maxHeight: '200px'
              }}
            />
            <button onClick={send} disabled={loading || !input.trim()} style={{
              padding: '0 1.25rem',
              borderRadius: '12px',
              border: 'none',
              background: input.trim() ? '#4a4ad8' : '#2a2a4a',
              color: '#fff',
              fontWeight: 600,
              cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              flexShrink: 0,
              height: '48px',
              fontSize: isMobile ? '0.85rem' : '1rem'
            }}>
              {isMobile ? '→' : 'Trimite'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
